// initial state
const state = () => ({
    forms: [],
    form: {},
    styles: {}
});

const JSON_FIELDS = [
    "font",
    "colorScheme",
    "loanTypeSelected",
    "campaign",
    "singleLender"
];

const SCALAR_FIELDS = [
    "bgColor",
    "headerColor",
    "labelColor",
    "primaryColor",
    "secondaryColor",
    "disclaimerColor",
    "borderSize",
    "borderRadius",
    "logoUrl",
    "title1",
    "btnColor",
    "btnBorderColor",
    "btnTxtColor",
    "btnRadius",
    "btnSize",
    "btnTxt",
    "showLogo",
    "showLoanType",
    "logoType"
];

// Multipart body for POST /forms/{form_id}/template. The server takes affid and
// api_key from the DB, so they are never sent.
const buildTemplateFormData = styles => {
    const fd = new FormData();
    SCALAR_FIELDS.forEach(key => {
        const v = styles[key];
        // Like JSON.stringify did, leave out anything that is undefined.
        if (v === undefined) {
            return;
        }
        fd.append(key, v === null ? "" : String(v));
    });
    JSON_FIELDS.forEach(key => {
        if (styles[key] !== undefined) {
            fd.append(key, JSON.stringify(styles[key]));
        }
    });
    if (styles.logo instanceof File) {
        fd.append("logo", styles.logo);
    }
    return fd;
};

// Turn an axios error into a message a person can read.
const errorMessage = (error, fallback) => {
    const data = error && error.response && error.response.data;
    if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (first && first[0]) {
            return first[0];
        }
    }
    if (data && data.message) {
        return data.message;
    }
    return fallback;
};

const saveTemplate = (formId, styles) =>
    axios
        .post("/forms/" + formId + "/template", buildTemplateFormData(styles))
        .then(() => "success");

const getters = {};

const actions = {
    getForms(context) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        return new Promise((resolve, reject) => {
            axios
                .get("/forms")
                .then(response => {
                    context.commit("getForms", response.data);
                })
                .catch(error => {
                    console.log(error);
                });
        });
    },

    createForm(context, data) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;

        return new Promise((resolve, reject) => {
            axios
                .post("/forms", {
                    type: data.type,
                    name: data.name
                })
                .then(response => {
                    if (response.status == 200) {
                        const templateData = {
                            formId: response.data.form_id,
                            font: {
                                category: "sans-serif",
                                family: "Lato"
                            },
                            bgColor: "#ffffff",
                            headerColor: data.headerColor,
                            labelColor: data.labelColor,
                            primaryColor: data.primaryColor,
                            secondaryColor: data.secondaryColor,
                            disclaimerColor: "#000000",
                            colorScheme: data.colorScheme,
                            borderRadius: "0px",
                            borderSize: "1px",
                            showLogo: data.logo != "" ? true : false,
                            logo: data.logo,
                            logoUrl:
                                data.logo != ""
                                    ? `https://${process.env.MIX_DO_SPACES_BUCKET}.${process.env.MIX_DO_SPACES_URL}/templates/${response.data.form_id}/${response.data.form_id}.${data.logoType}`
                                    : "",
                            logoType: data.logoType,
                            title1: "Get approved for a Personal Loan today!",
                            showLoanType: true,
                            loanTypeSelected: [
                                {
                                    type: "Debt Consolidation",
                                    icon: "debt",
                                    selected: true
                                },
                                {
                                    type: "Pay off Credit Cards",
                                    icon: "creditCard",
                                    selected: true
                                },
                                {
                                    type: "Home Improvement",
                                    icon: "home",
                                    selected: true
                                },
                                {
                                    type: "Purchase an Item",
                                    icon: "purchase",
                                    selected: true
                                },
                                {
                                    type: "Grow Your Business",
                                    icon: "business",
                                    selected: true
                                },
                                {
                                    type: "Cover Medical Expenses",
                                    icon: "medical",
                                    selected: true
                                },
                                {
                                    type: "Pay for a Wedding",
                                    icon: "wedding",
                                    selected: true
                                },
                                {
                                    type: "Buy a Car",
                                    icon: "car",
                                    selected: true
                                },
                                {
                                    type: "Take a Vacation",
                                    icon: "vacation",
                                    selected: true
                                },
                                {
                                    type: "Bills and Expenses",
                                    icon: "bills",
                                    selected: true
                                },
                                {
                                    type: "Rebuild Your Credit",
                                    icon: "rebuild",
                                    selected: true
                                },
                                {
                                    type: "Other",
                                    icon: "other",
                                    selected: true
                                }
                            ],
                            // Button Options
                            btnColor: data.primaryColor,
                            btnBorderColor: data.secondaryColor,
                            btnTxtColor: "#FFFFFF",
                            btnRadius: "0px",
                            btnSize: "50px",
                            btnTxt: "Apply Now",
                            // Single Lender options
                            singleLender: {
                                is_active: false,
                                name: "",
                                id: "",
                                logo: ""
                            },
                            campaign: {
                                ext_a: "",
                                ext_b: "",
                                ext_c: "",
                                ext_gid: "",
                                ext_pid: "",
                                utm_source: "",
                                utm_content: "",
                                utm_medium: "",
                                utm_campaign: "",
                                utm_term: ""
                            }
                        };

                        saveTemplate(response.data.form_id, templateData)
                            .then(() => resolve(response.data.id))
                            .catch(error => {
                                // Do not leave a form without a template behind
                                context.dispatch("deleteForm", response.data.id);
                                reject(
                                    errorMessage(
                                        error,
                                        "Could not save the form template."
                                    )
                                );
                            });
                    } else {
                        console.log(response);
                    }
                })
                .catch(error => {
                    console.log(error);
                    reject(errorMessage(error, "Could not create the form."));
                });
        });
    },

    deleteForm(context, id) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        axios
            .delete("/forms/" + id)
            .then(response => {
                context.commit("deleteForm", id);
            })
            .catch(error => {
                console.log(error);
            });
    },

    getForm(context, id) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        axios
            .get("/forms/" + id)
            .then(response => {
                context.commit("getForm", response.data);
            })
            .catch(error => {
                console.log(error);
                window.location.href = "/forms";
            });
    },

    updateForm(context, form) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        axios
            .patch("/forms/" + form.id, {
                id: form.id,
                user_id: form.user_id,
                is_active: form.is_active,
                name: form.name
            })
            .then(response => {
                context.commit("updateForm", response.data);
            })
            .catch(error => {
                console.log(error);
            });
    },

    duplicateForm: async (context, id) => {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        return new Promise((resolve, reject) => {
            axios
                .post("/duplicate-form/", {
                    id: id
                })
                .then(response => {
                    // The server copies the template files too
                    context.commit("updateForms", response.data.new_form);
                    resolve("success");
                })
                .catch(error => {
                    console.log(error);
                    reject(
                        errorMessage(error, "Could not duplicate the form.")
                    );
                });
        });
    },

    // Pull json file with main styles from S3
    getStyle(context, id) {
        return fetch(
            `https://${process.env.MIX_DO_SPACES_BUCKET}.${process.env.MIX_DO_SPACES_URL}/templates/${id}/${id}.json`
        )
            .then(response => {
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response.json();
            })
            .then(response => {
                context.commit("getStyle", response);
            })
            .catch(error => {
                console.log("error", error);
            });
    },
    // Update css and json styles on the server
    updateStyle(context, styles) {
        axios.defaults.headers.common["Authorization"] =
            "Bearer " + context.rootState.auth.token;
        return saveTemplate(styles.formId, styles).catch(error => {
            throw errorMessage(error, "Could not save the form template.");
        });
    }
};

const mutations = {
    getForms(state, forms) {
        state.forms = forms;
    },
    deleteForm(state, id) {
        const index = state.forms.findIndex(item => item.id == id);
        // Not listed (e.g. a createForm rollback): splice(-1) would drop the last form.
        if (index >= 0) {
            state.forms.splice(index, 1);
        }
    },
    getForm(state, form) {
        state.form = form;
    },
    updateForm(state, form) {
        state.form = form;
    },
    getStyle(state, styles) {
        // In case the template don't have campaign we add
        if (!styles.campaign) {
            styles.campaign = {
                ext_a: "",
                ext_b: "",
                ext_c: "",
                ext_gid: "",
                ext_pid: "",
                utm_source: "",
                utm_content: "",
                utm_medium: "",
                utm_campaign: "",
                utm_term: ""
            };
        }
        state.styles = styles;
    },
    updateForms(state, form) {
        state.forms = [...state.forms, form];
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
